import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { DensityMode } from "@/contexts/AdaptiveViewportContext";
import { BillingPeriod } from "@/types/plans";
import { Check } from "lucide-react";

interface AlternativePlanCardProps {
  planId: string;
  planName: string;
  planTier?: 'premium' | 'standard';
  miniSummary: string;
  price?: number;
  isCurrent: boolean;
  isFree: boolean;
  badge?: {
    text: string;
    className: string;
  };
  densityMode?: DensityMode;
  onSelect?: (planId: string, period: BillingPeriod) => void;
  isSelected?: boolean;
  priceOptions?: Array<{
    period: BillingPeriod;
    price: number;
    originalPrice?: number;
    savings?: number;
  }>;
  selectedOption?: { planId: string; period: BillingPeriod } | null;
  isExpanded?: boolean;
}

const PERIOD_LABELS: Record<BillingPeriod, { short: string; full: string }> = {
  weekly: { short: 'Semanal', full: 'Semanal' },
  monthly: { short: 'Mensal', full: 'Mensal' },
  quarterly: { short: 'Trimestral', full: 'Trimestral' },
};

export const AlternativePlanCard = ({
  planId,
  planName,
  planTier = 'standard',
  miniSummary,
  price,
  isCurrent,
  isFree,
  badge,
  densityMode = 'expanded',
  onSelect,
  isSelected = false,
  priceOptions,
  selectedOption,
  isExpanded = true,
}: AlternativePlanCardProps) => {
  const isClickable = !isFree && onSelect;
  
  const handleClick = () => {
    if (isClickable && priceOptions && priceOptions.length > 0) {
      // Default to monthly period
      onSelect(planId, 'monthly');
    }
  };
  
  const handlePeriodSelect = (period: BillingPeriod) => {
    if (onSelect) {
      onSelect(planId, period);
    }
  };
  
  // Modo compacto: 1 linha clicável + mini-seletor quando selecionado
  if (densityMode === 'compact') {
    return (
      <div className={cn(
        "rounded-lg border transition-all duration-200",
        isFree && "bg-white/10 border-white/20",
        !isFree && !isSelected && "bg-white/5 border-white/10",
        isClickable && "cursor-pointer",
        // Estilo segue o tier mesmo na posição Alternative
        isSelected && !isFree && planTier === 'premium' && "border-luxury-pinkBright/50 bg-luxury-pinkBright/10 shadow-[0_0_20px_rgba(233,30,99,0.3)]",
        isSelected && !isFree && planTier === 'standard' && "border-amber-500/50 bg-amber-500/10 shadow-[0_0_15px_rgba(245,158,11,0.2)]"
      )}>
        {/* Linha principal - sempre visível */}
        <div 
          onClick={handleClick}
          className="flex items-center justify-between px-3 py-2"
        >
          <div className="flex items-center gap-2">
            {isSelected && !isFree && (
              <Check className={cn(
                "w-4 h-4 flex-shrink-0",
                planTier === 'premium' ? "text-luxury-pinkBright" : "text-amber-400"
              )} />
            )}
            <span className="text-sm text-white/80">▸ {planName}</span>
          </div>
          
          {price && !isFree && (
            <span className="text-xs text-white/60">R${price.toFixed(2)}/mês</span>
          )}
          {isFree && (
            <span className="text-xs text-white/60">Grátis</span>
          )}
        </div>
        
        {/* Mini-seletor de período - aparece quando selecionado */}
        {isSelected && !isFree && priceOptions && priceOptions.filter(opt => opt.price > 0).length > 0 && (
          <div className={cn(
            "grid gap-2 px-3 pb-2 pt-1 border-t border-white/10",
            priceOptions.filter(opt => opt.price > 0).length === 2 ? "grid-cols-2" : "grid-cols-3"
          )}>
            {priceOptions.filter(opt => opt.price > 0).map((option) => {
              const isPeriodSelected = selectedOption?.planId === planId && selectedOption?.period === option.period;
              
              return (
                <button
                  key={option.period}
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    handlePeriodSelect(option.period); 
                  }}
                  className={cn(
                    "text-xs py-1.5 px-2 rounded border transition-all",
                    isPeriodSelected && planTier === 'premium' && "border-luxury-pinkBright bg-luxury-pinkBright/20 text-white font-semibold",
                    isPeriodSelected && planTier === 'standard' && "border-amber-500 bg-amber-500/20 text-white font-semibold",
                    !isPeriodSelected && "border-white/10 text-white/60 hover:border-white/20"
                  )}
                >
                  <div className="flex flex-col items-center">
                    <span className="font-bold">{PERIOD_LABELS[option.period].short}</span>
                    <span className="text-[10px]">R${option.price.toFixed(2)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Modo normal: compacto mas com seletor de períodos
  if (densityMode === 'normal') {
    return (
      <div 
        onClick={handleClick}
        className={cn(
          "rounded-xl p-3 border transition-all duration-200",
          isFree && "bg-white/10 border-white/20",
          !isFree && !isSelected && "bg-white/5 border-white/10",
          isClickable && planTier === 'premium' && "cursor-pointer hover:border-luxury-pinkBright/30",
          isClickable && planTier === 'standard' && "cursor-pointer hover:border-amber-500/30",
          isSelected && !isFree && planTier === 'premium' && "border-luxury-pinkBright/50 bg-luxury-pinkBright/5",
          isSelected && !isFree && planTier === 'standard' && "border-amber-500/50 bg-amber-500/5"
        )}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex-1">
            <h4 className="text-sm font-bold text-white">{planName}</h4>
            {badge && (
              <Badge className={cn("text-[9px] font-semibold mt-1", badge.className)}>
                {badge.text}
              </Badge>
            )}
          </div>
          {isSelected && !isFree && (
            <Check className={cn(
              "w-4 h-4",
              planTier === 'premium' ? "text-luxury-pinkBright" : "text-amber-400"
            )} />
          )}
        </div>
        
        {/* Seletor de períodos compacto */}
        {!isFree && priceOptions && priceOptions.filter(opt => opt.price > 0).length > 0 && (
          <div className={cn(
            "grid gap-1.5",
            priceOptions.filter(opt => opt.price > 0).length === 2 ? "grid-cols-2" : "grid-cols-3"
          )}>
            {priceOptions.filter(opt => opt.price > 0).map((option) => {
              const isPeriodSelected = selectedOption?.planId === planId && selectedOption?.period === option.period;
              
              return (
                <button
                  key={option.period}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePeriodSelect(option.period);
                  }}
                  className={cn(
                    "text-xs py-1.5 px-2 rounded border transition-all",
                    isPeriodSelected && planTier === 'premium' && "border-luxury-pinkBright bg-luxury-pinkBright/20 text-white font-semibold",
                    isPeriodSelected && planTier === 'standard' && "border-amber-500 bg-amber-500/20 text-white font-semibold",
                    !isPeriodSelected && "border-white/10 text-white/60 hover:border-white/20"
                  )}
                >
                  <div className="flex flex-col items-center">
                    <span className="font-bold">{PERIOD_LABELS[option.period].short}</span>
                    <span className="text-[10px]">R${option.price.toFixed(2)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
        
        {isFree && (
          <div className="text-xs text-white/60 mt-1">Continue grátis</div>
        )}
      </div>
    );
  }

  // Modo expandido: completo com colapso inteligente
  return (
    <div 
      onClick={handleClick}
      className={cn(
        "rounded-xl p-4 border transition-all duration-200 relative",
        isFree && "bg-white/10 border-white/20",
        !isFree && !isSelected && "bg-white/5 border-white/10",
        // Estilo segue o tier mesmo na posição Alternative
        isClickable && planTier === 'premium' && "cursor-pointer hover:border-luxury-pinkBright/30 hover:shadow-[0_0_10px_rgba(233,30,99,0.2)]",
        isClickable && planTier === 'standard' && "cursor-pointer hover:border-amber-500/30 hover:shadow-[0_0_10px_rgba(245,158,11,0.2)]",
        isSelected && !isFree && planTier === 'premium' && "border-luxury-pinkBright/50 bg-luxury-pinkBright/5 shadow-[0_0_15px_rgba(233,30,99,0.3)]",
        isSelected && !isFree && planTier === 'standard' && "border-amber-500/50 bg-amber-500/5 shadow-[0_0_15px_rgba(245,158,11,0.3)]"
      )}
    >
      {/* Selection indicator */}
      {isSelected && !isFree && (
        <div className={cn(
          "absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center shadow-lg animate-pulse",
          planTier === 'premium' ? "bg-luxury-pinkBright" : "bg-amber-500"
        )}>
          <Check className="w-4 h-4 text-white" />
        </div>
      )}
      
      {/* Header */}
      <div className="flex items-center justify-between mb-1 md:mb-2">
        <h4 className="text-base md:text-lg font-bold text-white">{planName}</h4>
        {badge && (
          <Badge className={cn("text-[10px] font-semibold", badge.className)}>
            {badge.text}
          </Badge>
        )}
      </div>

      {/* Summary */}
      <div className={cn(
        "transition-all duration-300 ease-in-out overflow-hidden",
        isExpanded ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
      )}>
        <p className="text-xs text-white/60 mb-3">
          {miniSummary}
        </p>
      </div>

      {/* Price or Period Selector */}
      {isFree ? (
        <div className="text-sm font-medium text-white/70">
          Continue grátis →
        </div>
      ) : priceOptions && priceOptions.filter(opt => opt.price > 0).length > 0 ? (
        <div 
          className={cn(
            "grid grid-cols-1 gap-2 transition-all duration-300",
            priceOptions.filter(opt => opt.price > 0).length === 2 ? "md:grid-cols-2" : "md:grid-cols-3",
            isExpanded ? "mt-3" : "mt-0"
          )}
        >
          {priceOptions.filter(opt => opt.price > 0).map((option) => {
            const isPeriodSelected = selectedOption?.planId === planId && selectedOption?.period === option.period;
            const isBestValue = !!(option.savings && option.savings > 0);
            
              // MOBILE: layout horizontal - com preços empilhados
              const mobileButton = (
                <button
                  key={`mobile-${option.period}`}
                  onClick={() => handlePeriodSelect(option.period)}
                  className={cn(
                    "w-full grid grid-cols-[24px_minmax(80px,100px)_1fr] items-center gap-1 md:gap-2 p-2 rounded-lg border transition-all duration-200",
                    isPeriodSelected && planTier === 'premium' && "border-luxury-pinkBright/50 bg-luxury-pinkBright/10",
                    isPeriodSelected && planTier === 'standard' && "border-amber-500/50 bg-amber-500/10",
                    !isPeriodSelected && "border-white/10 bg-white/5 hover:border-white/20"
                  )}
                >
                  {/* Radio */}
                  <div className={cn(
                    "w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0",
                    isPeriodSelected && planTier === 'premium' && "border-luxury-pinkBright",
                    isPeriodSelected && planTier === 'standard' && "border-amber-500",
                    !isPeriodSelected && "border-white/30"
                  )}>
                    {isPeriodSelected && (
                      <div className={cn(
                        "w-2.5 h-2.5 rounded-full",
                        planTier === 'premium' ? "bg-luxury-pinkBright" : "bg-amber-500"
                      )} />
                    )}
                  </div>
                  
                  {/* Período */}
                  <div className="text-[11px] md:text-xs font-bold text-white uppercase">
                    {PERIOD_LABELS[option.period].short}
                  </div>
                  
                  {/* Preços empilhados: Original em cima, Final embaixo */}
                  <div className="flex flex-col items-end gap-0.5">
                    {option.originalPrice && option.originalPrice > option.price && (
                      <div className="text-[10px] text-white/40 line-through">
                        R${option.originalPrice.toFixed(2)}
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2">
                      <span className={cn(
                        "font-bold text-sm",
                        isPeriodSelected && planTier === 'premium' && "text-luxury-pinkBright",
                        isPeriodSelected && planTier === 'standard' && "text-amber-500",
                        !isPeriodSelected && "text-white"
                      )}>
                        R${option.price.toFixed(2)}
                      </span>
                      
                      {isBestValue && option.savings && option.savings > 0 && (
                        <span className="text-[8px] font-bold text-green-400 bg-green-500/20 px-1 py-0.5 rounded shrink-0">
                          -{option.savings}%
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );

              // DESKTOP: layout vertical
              const desktopButton = (
                <button
                  key={`desktop-${option.period}`}
                  onClick={() => handlePeriodSelect(option.period)}
                  className={cn(
                    "relative rounded-lg border transition-all duration-200 overflow-hidden flex flex-col items-center justify-center p-2 min-h-[90px] w-full",
                    isPeriodSelected && planTier === 'premium' && "border-luxury-pinkBright/50 bg-luxury-pinkBright/10",
                    isPeriodSelected && planTier === 'standard' && "border-amber-500/50 bg-amber-500/10",
                    !isPeriodSelected && "border-white/10 bg-white/5 hover:border-white/20"
                  )}
                >
                  <div className="text-xs font-bold text-white uppercase w-auto mb-1 text-center">
                    {PERIOD_LABELS[option.period].short}
                  </div>
                  
                  {/* Preço original primeiro (riscado) */}
                  {option.originalPrice && option.originalPrice > option.price && (
                    <div className="text-center text-xs text-white/40 line-through">
                      R${option.originalPrice.toFixed(2)}
                    </div>
                  )}
                  
                  {/* Preço final (destaque) */}
                  <div className="text-center flex items-center justify-center gap-1.5">
                    <span className={cn(
                      "font-bold text-lg",
                      isPeriodSelected && planTier === 'premium' && "text-luxury-pinkBright",
                      isPeriodSelected && planTier === 'standard' && "text-amber-500",
                      !isPeriodSelected && "text-white"
                    )}>
                      R${option.price.toFixed(2)}
                    </span>
                    {isBestValue && option.savings && option.savings > 0 && (
                      <span className="text-[9px] font-bold text-green-400 bg-green-500/20 px-1 py-0.5 rounded">
                        -{option.savings}%
                      </span>
                    )}
                  </div>
                  
                  <div className="text-center text-[10px] text-white/50">
                    /{option.period === 'quarterly' ? 'trimestre' : option.period === 'weekly' ? 'semana' : 'mês'}
                  </div>
                </button>
              );
              
              return (
                <div key={option.period} className="w-full">
                  <div className="md:hidden w-full">
                    {mobileButton}
                  </div>
                  <div className="hidden md:block w-full">
                    {desktopButton}
                  </div>
                </div>
              );
          })}
        </div>
      ) : price ? (
        <div className="text-lg font-bold text-white">
          R${price.toFixed(2)}<span className="text-sm text-white/50">/mês</span>
        </div>
      ) : null}

      {/* Current badge */}
      {isCurrent && (
        <Badge className="mt-2 bg-white/10 text-white/60 border-white/20 text-xs">
          Atual
        </Badge>
      )}
    </div>
  );
};